import { gsap } from "gsap";
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

import DropDown from "../snippets/dropdown";
import cart from "../utils/cart";

export default class Product {
    constructor(container) {
        this.container = container;
        this.init();
    }

    init = () => {
        const dropdowns = [];
        this.container.querySelectorAll('.js-dropdown').forEach(element => {
            dropdowns.push(new DropDown(element, { button: element.querySelector('.js-button'), pane: element.querySelector('.js-pane'), isOpen: false, clickOutside: false }));
        });

        // Accordion behaviour: opening one meta tab closes any others that are
        // open. DropDown's own click handler (registered first) toggles this tab
        // open, then this handler closes the siblings.
        dropdowns.forEach(dropdown => {
            dropdown.button.addEventListener('click', () => {
                if (!dropdown.open) return; // a close — leave the others alone
                dropdowns.forEach(other => {
                    if (other !== dropdown && other.open) other.onHidePane();
                });
            });
        });

        // Two-column layout only: pin the info column while the images scroll.
        // The column pins at its TOP while it fits the space between the header
        // and the stick line, and at its BOTTOM once it's taller (so its full
        // height scrolls through before it sticks). Recomputed on resize/refresh.
        const infoWrap = document.querySelector('.product__info-wrap');
        if (infoWrap) {
            gsap.matchMedia().add('(min-width: 550px)', () => {
                const STICK_GAP = 24;
                const st = ScrollTrigger.create({
                    trigger: infoWrap,
                    start: () => {
                        // Switch top-pin <-> bottom-pin at the exact height where
                        // BOTH modes resolve to the same pinned position — the
                        // column's bottom resting on the stick line (viewport
                        // bottom - STICK_GAP). Using this crossover point (rather
                        // than a plain `height < viewport` test) means opening a
                        // tab that pushes the column past the threshold no longer
                        // teleports the column — the transition is seamless.
                        const available = window.innerHeight - infoWrap.offsetTop - STICK_GAP;
                        const fitsViewport = infoWrap.offsetHeight <= available;
                        return fitsViewport ? `top ${infoWrap.offsetTop}` : `bottom bottom-=${STICK_GAP}`;
                    },
                    end: 'bottom bottom',
                    endTrigger: '.product-images',
                    pin: true,
                    pinSpacer: false,
                    invalidateOnRefresh: true,
                });

                // Recalculate the column height / stick point whenever it changes
                // (tabs opening or closing, frame/variant changes) so "stick when
                // the right column ends" stays correct for the new height.
                // Debounced to run once the open/close animation has settled.
                //
                // IMPORTANT: while the pin is active GSAP freezes infoWrap's inline
                // height, so a ResizeObserver on infoWrap alone never sees a tab
                // open/close — the recompute would never fire and the opened tab
                // would overflow the frozen, fixed column below the fold. So we
                // also observe the meta-info container (a child that is NOT
                // height-locked), whose height DOES change when a tab toggles.
                let refreshTimer;
                const resizeObserver = new ResizeObserver(() => {
                    clearTimeout(refreshTimer);
                    refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 120);
                });
                resizeObserver.observe(infoWrap);
                const metaInfo = infoWrap.querySelector('.product__meta-info');
                if (metaInfo) resizeObserver.observe(metaInfo);

                return () => {
                    clearTimeout(refreshTimer);
                    resizeObserver.disconnect();
                    st.kill();
                };
            });
        }

        this.currency = this.container.querySelector('[data-submit-btn]')?.dataset.currency;
        this.form = this.container.querySelector('form');

        this.initOptionDropdowns();
        this.initFrameSelector();
        this.initQuantity();
        this.initSubmitPrice();
        this.initGallery();
        this.initSubmit();
    }

    // --- Add-to-bag total price ---

    // The submit button shows the running total: variant price × quantity,
    // plus the selected frame's price × quantity. Works for every product
    // type — the hidden select[name="id"] always carries data-price.
    initSubmitPrice = () => {
        this.submitBtn = this.container.querySelector('[data-submit-btn]');
        this.submitLabel = this.submitBtn?.querySelector('[data-submit-label]');
        this.submitPriceEl = this.submitBtn?.querySelector('[data-submit-price]');
        this.variantSelect = this.container.querySelector('select[name="id"]');
        if (!this.submitPriceEl || !this.variantSelect) return;

        // Covers every variant change: the size dropdown dispatches 'change'
        // on the hidden select after updating it.
        this.variantSelect.addEventListener('change', () => this.updateSubmitPrice());
        this.updateSubmitPrice();
    }

    // The hidden select's selected option carries data-available.
    isSelectedVariantAvailable = () => {
        const selected = this.variantSelect?.options[this.variantSelect.selectedIndex];
        return !selected || selected.dataset.available !== 'false';
    }

    // Sold-out variant: disabled button reading "Out of stock", no price.
    // In stock: the normal "Add to Bag • price" button. Called after every
    // variant change and after an add-to-cart round trip.
    updateSubmitAvailability = () => {
        if (!this.submitBtn) return;
        const available = this.isSelectedVariantAvailable();
        const sep = this.submitBtn.querySelector('.submit-button__sep');
        this.submitBtn.disabled = !available;
        this.submitBtn.classList.toggle('submit-button--sold-out', !available);
        if (this.submitLabel) {
            this.submitLabel.textContent = available
                ? (this.submitBtn.dataset.labelAdd || 'Add to Bag')
                : (this.submitBtn.dataset.labelSoldOut || 'Out of stock');
        }
        if (sep) sep.hidden = !available;
        if (this.submitPriceEl) this.submitPriceEl.hidden = !available;

        // Quantity is meaningless for a sold-out variant: lock it too.
        const quantityWrap = this.container.querySelector('[data-quantity-wrap]');
        if (quantityWrap) {
            quantityWrap.classList.toggle('quantity-wrap--disabled', !available);
            quantityWrap.querySelectorAll('button, input').forEach((el) => {
                el.disabled = !available;
            });
        }
    }

    updateSubmitPrice = () => {
        if (!this.submitPriceEl || !this.variantSelect) return;
        const quantity = parseInt(this.quantityInput?.value, 10) || 1;
        const selected = this.variantSelect.options[this.variantSelect.selectedIndex];
        const unitPrice = parseInt(selected?.dataset.price || '0', 10);
        const unitTotal = unitPrice + (this.framePrice || 0);
        this.submitPriceEl.textContent = this.formatPrice(unitTotal * quantity, this.submitBtn?.dataset.currency);
        // The top product price shows the same unit total (variant + frame),
        // without the quantity multiplier.
        this.updateDisplayedPrice(unitTotal);
        this.updateSubmitAvailability();
    }

    // --- Phone image gallery (main slider + thumbnails) ---

    // On phone the main image is a one-at-a-time swipeable slider (see
    // product.scss) with a thumbnail strip below. Tapping a thumb scrolls the
    // main slider to that image; swiping/dragging the main slider updates the
    // active thumbnail. All native scroll, so it's inert in the desktop/tablet
    // two-column layout (the main slider doesn't overflow there).
    initGallery = () => {
        const gallery = this.container.querySelector('[data-product-gallery]');
        if (!gallery) return;

        const main = gallery.querySelector('[data-gallery-main]');
        const getSlides = () => Array.from(gallery.querySelectorAll('[data-gallery-slide]:not([hidden])'));
        const getThumbs = () => Array.from(gallery.querySelectorAll('[data-gallery-thumb]:not([hidden])'));
        const slides = getSlides();
        const thumbs = getThumbs();
        if (!main || slides.length < 2) return;

        // Resolve index against live DOM so variant reorders stay in sync.
        const setActive = (idx) => {
            const currentThumbs = getThumbs();
            const clamped = Math.max(0, Math.min(currentThumbs.length - 1, idx));
            currentThumbs.forEach((t, i) => t.classList.toggle('is-active', i === clamped));
        };

        // Tap a thumbnail → scroll the main slider to that image.
        thumbs.forEach((thumb) => {
            thumb.addEventListener('click', () => {
                const i = getThumbs().indexOf(thumb);
                getSlides()[i]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
                setActive(i);
            });
        });

        // Keep the active thumbnail in sync with the image currently in view.
        let raf = null;
        main.addEventListener('scroll', () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = null;
                if (!main.clientWidth) return;
                setActive(Math.round(main.scrollLeft / main.clientWidth));
            });
        });

        // Mouse click-drag to scroll (touch/trackpad are native). Only engages
        // when the main slider actually overflows (i.e. the mobile layout).
        let isDown = false;
        let startX = 0;
        let startScroll = 0;
        let moved = false;

        main.addEventListener('mousedown', (e) => {
            if (main.scrollWidth <= main.clientWidth) return;
            isDown = true;
            moved = false;
            startX = e.pageX;
            startScroll = main.scrollLeft;
            main.classList.add('is-dragging');
            e.preventDefault();
        });

        window.addEventListener('mousemove', (e) => {
            if (!isDown) return;
            const dx = e.pageX - startX;
            if (Math.abs(dx) > 3) moved = true;
            main.scrollLeft = startScroll - dx;
        });

        const end = () => {
            isDown = false;
            main.classList.remove('is-dragging');
        };
        window.addEventListener('mouseup', end);
        main.addEventListener('mouseleave', end);

        // A drag shouldn't register as a click.
        main.addEventListener('click', (e) => {
            if (moved) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, true);
    }

    // --- Quantity ---

    initQuantity = () => {
        this.quantityInput = this.container.querySelector('input[name="quantity"]');
        if (!this.quantityInput) return;

        const minus = this.container.querySelector('.quantity-wrap .minus');
        const plus = this.container.querySelector('.quantity-wrap .plus');

        minus?.addEventListener('click', () => {
            const current = parseInt(this.quantityInput.value, 10) || 1;
            if (current > 1) this.quantityInput.value = current - 1;
            this.updateSubmitPrice();
        });

        plus?.addEventListener('click', () => {
            const current = parseInt(this.quantityInput.value, 10) || 1;
            this.quantityInput.value = current + 1;
            this.updateSubmitPrice();
        });

        // Validate on manual input
        this.quantityInput.addEventListener('change', () => {
            const val = parseInt(this.quantityInput.value, 10);
            if (!val || val < 1) this.quantityInput.value = 1;
            this.updateSubmitPrice();
        });
    }

    // --- Option custom dropdowns (one per product option) ---

    // Each product option (Poster Size, Language, ...) gets its own dropdown —
    // never a combined variant list. Selections are resolved back to a variant
    // via the variants JSON, and the size-named option drives the frame logic.
    initOptionDropdowns = () => {
        this.sizeSelect = this.container.querySelector('select[name="id"]');
        this.optionDropdowns = Array.from(this.container.querySelectorAll('[data-option-dropdown]'));
        if (!this.optionDropdowns.length) return;

        const dataEl = this.container.querySelector('[data-variants-json]');
        try {
            this.variantsData = dataEl ? JSON.parse(dataEl.textContent) : [];
        } catch {
            this.variantsData = [];
        }

        // Which option is the size (drives frame availability/pricing).
        this.sizeOptionIndex = this.optionDropdowns.findIndex(
            dropdown => /size/i.test(dropdown.dataset.optionName || '')
        );

        // Seed the current selection from the initially selected variant.
        const currentId = this.sizeSelect?.value;
        const current = this.variantsData.find(v => String(v.id) === String(currentId)) || this.variantsData[0];
        this.selectedOptions = current ? [...current.options] : [];

        this.optionDropdowns.forEach(dropdown => {
            const trigger = dropdown.querySelector('[data-option-trigger]');
            trigger?.addEventListener('click', () => this.toggleDropdown(dropdown));

            dropdown.querySelectorAll('[data-option-value]').forEach(item => {
                item.addEventListener('click', () => this.selectOption(dropdown, item));
            });
        });

        document.addEventListener('click', (e) => {
            this.optionDropdowns.forEach(dropdown => {
                if (!dropdown.contains(e.target)) this.closeDropdown(dropdown);
            });
        });

        this.updateOptionPrices();
        this.updateGalleryForVariant(current);
    }

    findVariant = (options) => {
        return this.variantsData.find(v => v.options.every((value, i) => value === options[i])) || null;
    }

    selectOption = (dropdown, item) => {
        const index = parseInt(dropdown.dataset.optionIndex, 10) - 1;
        const value = item.dataset.optionValue;
        this.selectedOptions[index] = value;

        // Resolve the variant for the new combination; if it doesn't exist,
        // fall back to the first variant carrying this value and adopt its
        // other option values.
        let variant = this.findVariant(this.selectedOptions);
        if (!variant) {
            variant = this.variantsData.find(v => v.options[index] === value);
            if (variant) this.selectedOptions = [...variant.options];
        }

        this.syncOptionDropdowns();

        if (variant && this.sizeSelect) {
            this.sizeSelect.value = variant.id;
            // The 'change' listener re-totals both the top price and the
            // add-to-bag price (variant + frame).
            this.sizeSelect.dispatchEvent(new Event('change'));
        }

        this.closeDropdown(dropdown);

        // Update frame availability and price for the (possibly new) size
        if (this.frameSelector) {
            this.updateFrameAvailability();
            this.updateFrameItemPrices();
            this.updateFramePrice(this.selectedFrame ? this.getCurrentFrameVariant() : null);
        }

        this.updateOptionPrices();
        this.updateGalleryForVariant(variant);
    }

    // Replace the first gallery image with the variant's assigned image.
    // Other variants' images are hidden so they don't stack under the hero.
    // No featured_image → leave the current first image as-is.
    updateGalleryForVariant = (variant) => {
        const gallery = this.container.querySelector('[data-product-gallery]');
        const main = gallery?.querySelector('[data-gallery-main]');
        if (!main) return;

        if (!this.galleryOriginalSlides) {
            this.galleryOriginalSlides = Array.from(main.querySelectorAll('[data-gallery-slide]'));
            this.galleryOriginalThumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
        }

        const featured = variant?.featured_image;
        if (!featured) return;

        const heroSlide = this.findGalleryItem(this.galleryOriginalSlides, featured);
        if (!heroSlide) return;

        const variantImageIds = new Set(
            (this.variantsData || [])
                .map((item) => item.featured_image?.id)
                .filter((id) => id != null)
                .map(String)
        );
        const heroId = String(heroSlide.dataset.imageId);

        this.galleryOriginalSlides.forEach((slide) => {
            const id = String(slide.dataset.imageId);
            slide.hidden = variantImageIds.has(id) && id !== heroId;
        });
        this.galleryOriginalThumbs.forEach((thumb) => {
            const id = String(thumb.dataset.imageId);
            thumb.hidden = variantImageIds.has(id) && id !== heroId;
        });

        if (main.firstElementChild !== heroSlide) {
            main.insertBefore(heroSlide, main.firstElementChild);
        }

        const thumbsWrap = gallery.querySelector('[data-gallery-thumbs]');
        const heroThumb = this.findGalleryItem(this.galleryOriginalThumbs, featured);
        if (thumbsWrap && heroThumb && thumbsWrap.firstElementChild !== heroThumb) {
            thumbsWrap.insertBefore(heroThumb, thumbsWrap.firstElementChild);
        }

        this.syncFrameOverlay();
        this.resetGalleryToStart();
    }

    findGalleryItem = (nodes, featured) => {
        if (!featured || !nodes?.length) return null;
        const id = featured.id != null ? String(featured.id) : '';
        if (id) {
            const byId = nodes.find((node) => String(node.dataset.imageId) === id);
            if (byId) return byId;
        }
        const file = String(featured.src || '').split('?')[0].split('/').pop();
        if (!file) return null;
        return nodes.find((node) => {
            const src = node.dataset.imageSrc || node.querySelector('img:not([data-frame-overlay])')?.getAttribute('src') || '';
            return src.includes(file);
        }) || null;
    }

    // Frame overlay + --framed class must stay on the first wrap only, or
    // this.frameOverlay (queried once in initFrameSelector) points at the wrong node.
    syncFrameOverlay = () => {
        const main = this.container.querySelector('[data-gallery-main]');
        if (!main) return;

        const slides = Array.from(main.querySelectorAll('[data-gallery-slide]'));
        if (!slides.length) return;

        const overlay = this.container.querySelector('[data-frame-overlay]');
        if (!overlay) return;

        slides.forEach((slide, i) => {
            slide.classList.toggle('product-image-wrap--framed', i === 0);
        });

        const first = slides[0];
        if (!first.contains(overlay)) first.appendChild(overlay);
        this.frameOverlay = overlay;
    }

    resetGalleryToStart = () => {
        const gallery = this.container.querySelector('[data-product-gallery]');
        if (!gallery) return;

        const main = gallery.querySelector('[data-gallery-main]');
        if (main) main.scrollLeft = 0;

        const firstVisibleThumb = gallery.querySelector('[data-gallery-thumb]:not([hidden])');
        gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
            thumb.classList.toggle('is-active', thumb === firstVisibleThumb);
        });
    }

    // Reflect this.selectedOptions in every dropdown's trigger text and
    // active item (a fallback pick can change more than one dropdown).
    syncOptionDropdowns = () => {
        this.optionDropdowns.forEach(dropdown => {
            const index = parseInt(dropdown.dataset.optionIndex, 10) - 1;
            const text = dropdown.querySelector('[data-option-text]');
            if (text) text.textContent = this.selectedOptions[index] || '';
            dropdown.querySelectorAll('[data-option-value]').forEach(item => {
                item.classList.toggle(
                    'product-form__dropdown-item--active',
                    item.dataset.optionValue === this.selectedOptions[index]
                );
            });
        });
    }

    // Next to each option value, show the price of the variant you'd get by
    // picking it (other selections unchanged; falls back to the first variant
    // with that value). Values with no variant at all are disabled; values
    // whose variant is sold out keep their price and stay selectable, only
    // greyed out - selecting one turns the add-to-bag button into a disabled
    // "Out of stock".
    updateOptionPrices = () => {
        this.optionDropdowns.forEach(dropdown => {
            const index = parseInt(dropdown.dataset.optionIndex, 10) - 1;
            dropdown.querySelectorAll('[data-option-value]').forEach(item => {
                const candidate = [...this.selectedOptions];
                candidate[index] = item.dataset.optionValue;
                const variant = this.findVariant(candidate)
                    || this.variantsData.find(v => v.options[index] === item.dataset.optionValue);
                const priceEl = item.querySelector('[data-option-price]');
                if (priceEl) priceEl.textContent = variant ? this.formatPrice(variant.price) : '';
                item.classList.toggle('product-form__dropdown-item--unavailable', !variant);
                item.classList.toggle('product-form__dropdown-item--sold-out', !!variant && variant.available === false);
            });
        });
    }

    // --- Frame custom dropdown ---

    initFrameSelector = () => {
        this.frameSelector = this.container.querySelector('[data-frame-selector]');
        if (!this.frameSelector) return;

        const jsonEl = this.frameSelector.querySelector('[data-frame-product-json]');
        if (!jsonEl) return;

        try {
            this.frameData = JSON.parse(jsonEl.textContent);
        } catch {
            return;
        }

        this.frameTrigger = this.frameSelector.querySelector('[data-frame-trigger]');
        this.frameDisplay = this.frameSelector.querySelector('[data-frame-display]');
        this.framePanel = this.frameSelector.querySelector('[data-frame-panel]');
        this.frameOptions = this.frameSelector.querySelectorAll('[data-frame-option], [data-frame-unframed]');
        this.frameOverlay = this.container.querySelector('[data-frame-overlay]');
        this.selectedFrame = null; // null = unframed
        this.framePrice = 0;

        // Build size→variants map from frame product. Index each variant under
        // every alias of its size ("A4", "21 x 29.7", handle-derived labels) so
        // ISO names and dimensions resolve to the same SKU. Variants may be
        // size-only or Size × Color — keep all variants per size and match the
        // selected frame color against option2 when present.
        // Color / option2 order is merchandiser-defined (Frame product
        // option.values in admin, plus the frame metaobject `order` field)
        // and is applied in Liquid — do not re-sort A–Z or by price here.
        this.frameHasColorOption = this.frameData.variants.some(v => v.option2);
        this.frameVariantMap = {};
        for (const variant of this.frameData.variants) {
            for (const sizeKey of this.sizeKeys(variant.option1 || variant.title)) {
                if (!this.frameVariantMap[sizeKey]) this.frameVariantMap[sizeKey] = [];
                this.frameVariantMap[sizeKey].push(variant);
            }
        }

        // Hide the whole selector when no frame supports any of this poster's
        // sizes — otherwise the dropdown renders with every option disabled.
        // Sizes come from the variants data (bare option values); the select's
        // option text is only a fallback for single-variant products.
        let productSizes = [];
        if (this.variantsData?.length && this.sizeOptionIndex >= 0) {
            productSizes = this.variantsData.map(v => v.options[this.sizeOptionIndex]);
        } else if (this.sizeSelect) {
            productSizes = Array.from(this.sizeSelect.options).map(option => {
                const text = option.textContent || '';
                return text.split(' - ')[0].trim();
            });
        }
        const anyFrameFits = this.frameData.frames.some(frame =>
            (frame.sizes || []).some(size => productSizes.some(ps => this.sizesMatch(ps, size))));
        if (!anyFrameFits) {
            this.frameSelector.style.display = 'none';
        }

        // Bind trigger
        this.frameTrigger.addEventListener('click', () => this.toggleDropdown(this.frameSelector));

        // Bind frame option clicks
        this.frameOptions.forEach(option => {
            option.addEventListener('click', () => this.selectFrame(option));
        });

        // Click outside to close
        document.addEventListener('click', (e) => {
            if (!this.frameSelector.contains(e.target)) {
                this.closeDropdown(this.frameSelector);
            }
        });

        // Initial state
        this.updateFrameAvailability();
        this.updateFrameItemPrices();
    }

    selectFrame = (option) => {
        // Update active state
        this.frameOptions.forEach(opt => opt.classList.remove('product-form__dropdown-item--active'));
        option.classList.add('product-form__dropdown-item--active');

        if (option.hasAttribute('data-frame-unframed')) {
            this.selectedFrame = null;
            this.framePrice = 0;
            this.updateFrameDisplay(null);
            this.hideFrameOverlay();
        } else {
            const handle = option.dataset.frameOption;
            const name = option.dataset.frameName;
            const frameInfo = this.frameData.frames.find(f => f.handle === handle);
            this.selectedFrame = {
                handle,
                name,
                thumbnail: frameInfo?.thumbnail || '',
                cartImage: frameInfo?.cart_image || ''
            };
            const frameVariant = this.getCurrentFrameVariant();
            this.framePrice = frameVariant?.price || 0;
            this.updateFrameDisplay(frameVariant);
            this.showFrameOverlay(frameInfo?.placeholder);
        }

        this.updateSubmitPrice();

        // Close dropdown
        this.closeDropdown(this.frameSelector);

    }

    updateFrameDisplay = (frameVariant) => {
        if (!this.frameDisplay) return;

        // Clear existing content
        while (this.frameDisplay.firstChild) {
            this.frameDisplay.removeChild(this.frameDisplay.firstChild);
        }

        if (!this.selectedFrame) {
            // Show unframed thumbnail
            const unframedSrc = this.container.querySelector('[data-frame-unframed] img')?.src;
            if (unframedSrc) {
                const thumbSpan = document.createElement('span');
                thumbSpan.className = 'product-form__dropdown-item-thumb';
                const img = document.createElement('img');
                img.src = unframedSrc;
                img.alt = 'Unframed';
                img.width = 36;
                img.height = 36;
                thumbSpan.appendChild(img);
                this.frameDisplay.appendChild(thumbSpan);
            }

            const textSpan = document.createElement('span');
            textSpan.className = 'product-form__dropdown-text';
            textSpan.textContent = 'Unframed';
            this.frameDisplay.appendChild(textSpan);
            return;
        }

        // Add thumbnail if available
        if (this.selectedFrame.thumbnail) {
            const thumbSpan = document.createElement('span');
            thumbSpan.className = 'product-form__dropdown-item-thumb';
            const img = document.createElement('img');
            img.src = this.selectedFrame.thumbnail;
            img.alt = this.selectedFrame.name;
            img.width = 36;
            img.height = 36;
            thumbSpan.appendChild(img);
            this.frameDisplay.appendChild(thumbSpan);
        }

        // Add name + price text
        const textSpan = document.createElement('span');
        textSpan.className = 'product-form__dropdown-text';

        const nameText = document.createTextNode(this.selectedFrame.name + '    ');
        textSpan.appendChild(nameText);

        if (frameVariant) {
            const currency = this.frameData.currency || 'EUR';
            const priceSpan = document.createElement('span');
            priceSpan.style.color = '#32323233';
            priceSpan.textContent = '+' + this.formatPrice(frameVariant.price, currency);
            textSpan.appendChild(priceSpan);
        }

        this.frameDisplay.appendChild(textSpan);
    }

    // --- Frame overlay ---

    showFrameOverlay = (placeholderUrl) => {
        if (!this.frameOverlay || !placeholderUrl) return;
        this.frameOverlay.src = placeholderUrl;
        this.frameOverlay.onload = () => {
            this.frameOverlay.classList.add('is-visible');
        };
        // If already cached, onload fires synchronously
        if (this.frameOverlay.complete) {
            this.frameOverlay.classList.add('is-visible');
        }
    }

    hideFrameOverlay = () => {
        if (!this.frameOverlay) return;
        this.frameOverlay.classList.remove('is-visible');
    }

    // --- Shared dropdown helpers ---

    toggleDropdown = (dropdown) => {
        const isOpen = dropdown.classList.contains('product-form__dropdown--open');
        // Close all dropdowns first
        this.container.querySelectorAll('.product-form__dropdown--open').forEach(d => {
            d.classList.remove('product-form__dropdown--open');
        });
        if (!isOpen) {
            dropdown.classList.add('product-form__dropdown--open');
        }
    }

    closeDropdown = (dropdown) => {
        dropdown.classList.remove('product-form__dropdown--open');
    }

    // --- Frame logic helpers ---

    // ISO 216 in millimetres. Retail frames often round A4 to 21×30 cm.
    ISO_PAPER_MM = {
        a0: [841, 1189],
        a1: [594, 841],
        a2: [420, 594],
        a3: [297, 420],
        a4: [210, 297],
        a5: [148, 210],
        a6: [105, 148],
    }

    normalizeSize = (str) => {
        if (str == null || str === '') return '';
        return String(str)
            .replace(/[\u00a0\u202f\u2007]/g, ' ')
            .replace(/(\d),(\d)/g, '$1.$2')
            .replace(/-/g, ' ')
            .replace(/(\d) (\d)/g, '$1.$2')
            .replace(/\s*[×x]\s*/gi, ' x ')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase()
            // Metaobject handles like "a-4" are emitted as "a 4". Collapse
            // that to "a4" so it matches poster options named "A4".
            .replace(/\ba\s+([0-6])\b/g, 'a$1');
    }

    // Every comparable token for a size label so "A4", "A4 ", "21 × 29.7 cm"
    // and handle-derived "a4 21 x 29.7 cm" hit the same frame SKU — without
    // treating unrelated poster sizes (30×40, 50×70, …) as equal.
    sizeKeys = (str) => {
        const normalized = this.normalizeSize(str);
        if (!normalized) return [];
        const keys = new Set([normalized]);

        const iso = normalized.match(/\ba([0-6])\b/);
        if (iso) keys.add(`a${iso[1]}`);

        const dimMatch = normalized.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/);
        if (dimMatch) {
            let w = parseFloat(dimMatch[1]);
            let h = parseFloat(dimMatch[2]);
            const hasMm = /\bmm\b/.test(normalized);
            const hasCm = /\bcm\b/.test(normalized);
            const asMm = hasMm || (!hasCm && w >= 105 && h >= 105);
            if (!asMm) {
                w *= 10;
                h *= 10;
            }
            const dims = w <= h ? [w, h] : [h, w];
            keys.add(this.normalizeSize(`${dims[0] / 10} x ${dims[1] / 10}`));
            for (const [name, mm] of Object.entries(this.ISO_PAPER_MM)) {
                const isoDims = mm[0] <= mm[1] ? mm : [mm[1], mm[0]];
                if (Math.abs(dims[0] - isoDims[0]) <= 5 && Math.abs(dims[1] - isoDims[1]) <= 5) {
                    keys.add(name);
                }
            }
        }

        for (const name of [...keys]) {
            const mm = this.ISO_PAPER_MM[name];
            if (mm) keys.add(this.normalizeSize(`${mm[0] / 10} x ${mm[1] / 10}`));
        }

        return [...keys];
    }

    sizesMatch = (a, b) => {
        const aKeys = this.sizeKeys(a);
        if (!aKeys.length) return false;
        const bKeys = new Set(this.sizeKeys(b));
        return aKeys.some(k => bKeys.has(k));
    }

    normalizeColor = (str) => {
        return String(str || '')
            .replace(/[\u00a0\u202f\u2007]/g, ' ')
            .replace(/[-_]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
    }

    getCurrentSizeTitle = () => {
        // The bare size value from the per-option dropdowns (the size-named
        // option if there is one, the first option otherwise).
        if (this.selectedOptions?.length) {
            if (this.sizeOptionIndex >= 0) return this.selectedOptions[this.sizeOptionIndex];
            return this.selectedOptions[0];
        }
        // Single-variant products: derive from the hidden select's option text.
        if (!this.sizeSelect) return null;
        const selected = this.sizeSelect.options[this.sizeSelect.selectedIndex];
        const text = selected?.textContent?.trim() || '';
        return text.split(' - ')[0].trim();
    }

    getCurrentFrameVariant = () => {
        const sizeTitle = this.getCurrentSizeTitle();
        if (!sizeTitle) return null;

        const seen = new Set();
        const candidates = [];
        for (const key of this.sizeKeys(sizeTitle)) {
            for (const variant of (this.frameVariantMap[key] || [])) {
                if (seen.has(variant.id)) continue;
                seen.add(variant.id);
                candidates.push(variant);
            }
        }
        if (!candidates.length) return null;

        // Size × Color mode: pick the variant whose color matches the selected
        // frame (option2 must equal the frame metaobject's Type value).
        if (this.frameHasColorOption && this.selectedFrame) {
            const name = this.normalizeColor(this.selectedFrame.name);
            const match = candidates.find(v => this.normalizeColor(v.option2) === name);
            if (match) return match;
        }

        return candidates[0];
    }

    updateDisplayedPrice = (priceInCents) => {
        const priceEl = this.container.querySelector('[data-product-price]');
        if (!priceEl) return;
        const currency = this.frameData?.currency || 'EUR';
        priceEl.textContent = this.formatPrice(priceInCents, currency);
    }

    formatPrice = (priceInCents, currency) => {
        currency = currency || this.currency || this.frameData?.currency || 'EUR';
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency
        }).format(priceInCents / 100);
    }

    updateFrameAvailability = () => {
        const sizeTitle = this.getCurrentSizeTitle();

        this.frameOptions.forEach(option => {
            if (option.hasAttribute('data-frame-unframed')) return;

            const handle = option.dataset.frameOption;
            const frameInfo = this.frameData.frames.find(f => f.handle === handle);

            const available = !!sizeTitle && frameInfo?.sizes?.length > 0 && frameInfo.sizes.some(s => this.sizesMatch(s, sizeTitle));

            option.classList.toggle('product-form__dropdown-item--unavailable', !available);
            option.disabled = !available;

            // Reset to unframed if current selection becomes unavailable
            if (!available && this.selectedFrame?.handle === handle) {
                const unframed = this.frameSelector.querySelector('[data-frame-unframed]');
                if (unframed) this.selectFrame(unframed);
            }
        });
    }

    updateFrameItemPrices = () => {
        const frameVariant = this.getCurrentFrameVariant();
        const currency = this.frameData?.currency || 'EUR';
        const price = frameVariant ? this.formatPrice(frameVariant.price, currency) : '';

        this.frameSelector.querySelectorAll('[data-frame-item-price]').forEach(el => {
            el.textContent = price ? '+' + price : '';
        });
    }

    updateFramePrice = (frameVariant) => {
        this.framePrice = frameVariant?.price || 0;
        this.updateFrameDisplay(frameVariant);
        // Re-total after the frame price changed for the new size (the select's
        // 'change' listener has already run at this point with the old price).
        this.updateSubmitPrice();
    }

    // --- Form submission ---

    // Always intercept the Shopify product form. Without this, the native POST
    // to /cart/add redirects to /cart — and this theme has no cart page, only
    // the drawer. Frame-selector setup is optional; add-to-bag is not.
    initSubmit = () => {
        if (!this.form) return;
        this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    handleSubmit = async (e) => {
        e.preventDefault();

        const variantSelect = this.sizeSelect || this.container.querySelector('select[name="id"]');
        const posterVariantId = variantSelect?.value;
        // A sold-out variant never reaches the cart (the button is disabled,
        // but a stale click or keyboard submit must be stopped too).
        if (!this.isSelectedVariantAvailable()) return;
        const quantity = parseInt(this.container.querySelector('input[name="quantity"]')?.value || '1', 10);

        if (!posterVariantId) return;

        const items = [{ id: parseInt(posterVariantId, 10), quantity }];

        if (this.selectedFrame) {
            const frameVariant = this.getCurrentFrameVariant();
            if (frameVariant) {
                // "_frame_image" (underscore = hidden from customers) lets the
                // cart drawer show the selected color instead of the generic
                // Frame product image, since color is not a variant dimension.
                const properties = { "Frame": this.selectedFrame.name };
                if (this.selectedFrame.cartImage) {
                    properties["_frame_image"] = this.selectedFrame.cartImage;
                }
                items.push({
                    id: frameVariant.id,
                    quantity,
                    properties
                });
            }
        }

        const submitBtn = this.container.querySelector('[data-submit-btn]');
        const setLabel = (text) => {
            if (this.submitLabel) this.submitLabel.textContent = text;
        };

        try {
            if (submitBtn) {
                submitBtn.disabled = true;
                setLabel('Adding...');
            }

            await cart.add(items);

            if (submitBtn) {
                setLabel('Added!');
                // Re-derive the button state: the selection may have changed
                // to a sold-out variant while "Added!" was showing.
                setTimeout(() => this.updateSubmitAvailability(), 1500);
            }

            // Open cart drawer
            if (window.cartDrawerInstance) {
                setTimeout(() => window.cartDrawerInstance.open(), 300);
            }
        } catch (error) {
            console.error('Failed to add to cart:', error);
            this.updateSubmitAvailability();
        }
    }
}
